/**
 * LeftShield Portal - Finding Detail Page
 * Route: /security/findings/:referenceId | Category: Risk / Code
 *
 * §12746 Wave 2 — GUID-routed detail page for `Finding`, replacing the local `DetailDrawer`
 * instances on `SecurityPage` (Findings tab) and `DependenciesPage` (Container Images tab, which
 * renders the same `Finding` entity with `type: 'DependencyRisk'`) for direct-link / deep-link
 * access. Shape follows the reference migration `src/pages/AIDecisionDetailPage.tsx`.
 *
 * FEATURE-PORTING PARITY — every field/section/badge rendered by either inline drawer is ported
 * here (union of both, since this single page now serves both chevrons):
 *   - Title & Control (controlId/frameworkCode) + Title
 *   - Badges: severity, status, type
 *   - Description (message)
 *   - Location (filePath/line/column, OR resourceType/resourceId/resourceName)
 *   - Code Snippet
 *   - Source (repository, branch, commitSha)
 *   - Metadata: First Seen, Last Seen, Occurrences, Due Date
 *   - Resolution (resolutionType, resolutionNotes, resolvedAt)
 *   - Suppression (existing suppression details, OR the "Request Suppression" affordance +
 *     dialog for users holding a suppression-request role, mirroring
 *     `FindingsPortalController.Suppress` `RequireAnyRole`)
 * Nothing omitted — this page is a strict superset of both source drawers.
 */
import React, { useCallback, useState } from 'react';
import {
  Title3,
  Body1,
  Body2,
  Caption1,
  Card,
  Badge,
  Button,
  Spinner,
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogContent,
  DialogActions,
  Field,
  Dropdown,
  Option,
  Textarea,
  MessageBar,
  MessageBarBody,
  tokens,
} from '@fluentui/react-components';
import { BugRegular, ErrorCircleRegular, DismissCircleRegular } from '@fluentui/react-icons';
import { useParams, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { usePageStyles } from '../../styles/page-styles';
import { PageHeader } from '../../components/common/PageHeader';
import { DetailSection, DetailField, DetailMetadataGrid, DetailBadges } from '../../components/common/DetailDrawer';
import { findingsApi, suppressionApi } from '../../services/api';
import { useAuth } from '../../features/authentication';
import type { SuppressionReason } from '../../types';

/** Code Magenta — `brand/brand-config.json` → `colors.categoryThemes.code.primary`. */
const FINDING_COLOR = '#E852B9';

/** Roles allowed to request a finding suppression — mirrors `FindingsPortalController.Suppress`
 *  `RequireAnyRole(...)` (same list as `SecurityPage.tsx`). */
const SUPPRESSION_REQUEST_ROLES = ['Organization.Admin', 'TenantAdmin', 'SecurityAnalyst', 'ComplianceManager'];

const severityColor = (severity: string): 'danger' | 'warning' | 'success' | 'informative' => {
  switch (severity?.toLowerCase()) {
    case 'critical':
    case 'high':
      return 'danger';
    case 'medium':
      return 'warning';
    case 'low':
      return 'success';
    default:
      return 'informative';
  }
};

const statusColor = (status: string): 'danger' | 'warning' | 'success' | 'informative' => {
  switch (status?.toLowerCase()) {
    case 'open':
      return 'danger';
    case 'in_progress':
    case 'confirmed':
      return 'warning';
    case 'resolved':
    case 'closed':
      return 'success';
    default:
      return 'informative';
  }
};

export const FindingDetailPage: React.FC = () => {
  const ps = usePageStyles();
  const { referenceId } = useParams<{ referenceId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const canRequestSuppression = user?.roles?.some((role) => SUPPRESSION_REQUEST_ROLES.includes(role)) ?? false;

  const [suppressDialogOpen, setSuppressDialogOpen] = useState(false);
  const [suppressReason, setSuppressReason] = useState<SuppressionReason>('FalsePositive');
  const [suppressJustification, setSuppressJustification] = useState('');
  const [suppressError, setSuppressError] = useState<string | null>(null);

  const {
    data: finding,
    isLoading,
    isError,
    refetch,
  } = useQuery({
    queryKey: ['finding-by-reference-id', referenceId],
    queryFn: () => findingsApi.getByReferenceId(referenceId!),
    enabled: !!referenceId,
  });

  const suppressMutation = useMutation({
    mutationFn: (params: { findingId: string; reason: string; justification: string }) =>
      suppressionApi.requestSuppression(params.findingId, params.reason, params.justification),
    onSuccess: () => {
      setSuppressDialogOpen(false);
      setSuppressReason('FalsePositive');
      setSuppressJustification('');
      setSuppressError(null);
      queryClient.invalidateQueries({ queryKey: ['finding-by-reference-id', referenceId] });
      queryClient.invalidateQueries({ queryKey: ['security-findings'] });
    },
    onError: (err: Error) => {
      setSuppressError(err.message || 'Failed to request suppression');
    },
  });

  const handleOpenSuppressDialog = useCallback(() => {
    setSuppressReason('FalsePositive');
    setSuppressJustification('');
    setSuppressError(null);
    setSuppressDialogOpen(true);
  }, []);

  const handleSubmitSuppression = useCallback(() => {
    if (!finding || !suppressJustification.trim()) return;
    suppressMutation.mutate({
      findingId: finding.id,
      reason: suppressReason,
      justification: suppressJustification.trim(),
    });
  }, [finding, suppressReason, suppressJustification, suppressMutation]);

  const backAction = (
    <Button appearance="subtle" onClick={() => navigate('/security')}>
      Back to Security
    </Button>
  );

  if (!referenceId) {
    return (
      <div className={ps.page}>
        <PageHeader
          title="Finding Detail"
          subtitle="No finding reference ID was provided."
          icon={<BugRegular />}
          iconColor={FINDING_COLOR}
          actions={backAction}
        />
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className={ps.page}>
        <PageHeader
          title="Finding Detail"
          subtitle="Loading security finding…"
          icon={<BugRegular />}
          iconColor={FINDING_COLOR}
        />
        <Card>
          <div className={ps.cardBody}>
            <Spinner label="Loading finding…" />
          </div>
        </Card>
      </div>
    );
  }

  if (isError || !finding) {
    return (
      <div className={ps.page}>
        <PageHeader
          title="Finding Detail"
          subtitle={`Finding ${referenceId}`}
          icon={<BugRegular />}
          iconColor={FINDING_COLOR}
          actions={backAction}
        />
        <Card>
          <div className={ps.cardBody}>
            <ErrorCircleRegular style={{ fontSize: '32px', color: tokens.colorPaletteRedForeground1 }} />
            <Body1>Unable to load this finding. It may not exist, or you may not have access to it.</Body1>
            <Button appearance="secondary" onClick={() => refetch()}>
              Retry
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  const f = finding;
  const hasLocation = !!(f.filePath || f.resourceId);
  const hasSource = !!(f.repository || f.branch);

  return (
    <div className={ps.page}>
      <PageHeader
        title="Finding Detail"
        subtitle={`${f.controlId} — ${f.frameworkCode}`}
        icon={<BugRegular />}
        iconColor={FINDING_COLOR}
        actions={backAction}
      />

      {/* Title, badges & description */}
      <Card>
        <div className={ps.cardBody}>
          <div className={ps.sectionHeader}>
            <Title3>{f.title}</Title3>
          </div>

          <DetailBadges>
            <Badge appearance="filled" color={severityColor(f.severity)}>
              {f.severity}
            </Badge>
            <Badge appearance="tint" color={statusColor(f.status)}>
              {f.status}
            </Badge>
            {f.type && <Badge appearance="outline">{f.type}</Badge>}
          </DetailBadges>

          {f.message && (
            <DetailField label="Description">
              <Body1>{f.message}</Body1>
            </DetailField>
          )}
        </div>
      </Card>

      {/* Location */}
      {hasLocation && (
        <Card>
          <div className={ps.cardBody}>
            <DetailSection title="Location">
              {f.filePath ? (
                <Body2 style={{ fontFamily: 'monospace' }}>
                  {f.filePath}
                  {f.line != null && `:${f.line}`}
                  {f.column != null && `:${f.column}`}
                </Body2>
              ) : (
                <Body2 style={{ fontFamily: 'monospace' }}>
                  {f.resourceType && `${f.resourceType} / `}
                  {f.resourceId}
                  {f.resourceName && ` (${f.resourceName})`}
                </Body2>
              )}
            </DetailSection>
          </div>
        </Card>
      )}

      {/* Code Snippet */}
      {f.codeSnippet && (
        <Card>
          <div className={ps.cardBody}>
            <DetailField label="Code Snippet">
              <pre
                style={{
                  fontFamily: 'monospace',
                  fontSize: '12px',
                  padding: tokens.spacingVerticalS,
                  borderRadius: tokens.borderRadiusMedium,
                  backgroundColor: tokens.colorNeutralBackground2,
                  overflow: 'auto',
                  margin: 0,
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-all',
                }}
              >
                {f.codeSnippet}
              </pre>
            </DetailField>
          </div>
        </Card>
      )}

      {/* Source */}
      {hasSource && (
        <Card>
          <div className={ps.cardBody}>
            <DetailField label="Source">
              <DetailBadges>
                {f.repository && <Body2>{f.repository}</Body2>}
                {f.branch && (
                  <Badge appearance="outline" size="small">
                    {f.branch}
                  </Badge>
                )}
                {f.commitSha && (
                  <Caption1 style={{ fontFamily: 'monospace' }}>{f.commitSha.substring(0, 8)}</Caption1>
                )}
              </DetailBadges>
            </DetailField>
          </div>
        </Card>
      )}

      {/* Metadata */}
      <Card>
        <div className={ps.cardBody}>
          <DetailSection>
            <DetailMetadataGrid>
              <DetailField label="First Seen">
                <Body2>{new Date(f.firstSeenAt).toLocaleString()}</Body2>
              </DetailField>
              <DetailField label="Last Seen">
                <Body2>{new Date(f.lastSeenAt).toLocaleString()}</Body2>
              </DetailField>
              <DetailField label="Occurrences">
                <Body2>{f.occurrenceCount}</Body2>
              </DetailField>
              {f.dueDate && (
                <DetailField label="Due Date">
                  <Body2>{new Date(f.dueDate).toLocaleDateString()}</Body2>
                </DetailField>
              )}
            </DetailMetadataGrid>
          </DetailSection>
        </div>
      </Card>

      {/* Resolution */}
      {f.resolutionType && (
        <Card>
          <div className={ps.cardBody}>
            <DetailSection title="Resolution">
              <Badge appearance="tint" color="success">
                {f.resolutionType}
              </Badge>
              {f.resolutionNotes && <Body2>{f.resolutionNotes}</Body2>}
              {f.resolvedAt && (
                <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>
                  Resolved {new Date(f.resolvedAt).toLocaleString()}
                </Caption1>
              )}
            </DetailSection>
          </div>
        </Card>
      )}

      {/* Suppression — request affordance is hidden (not disabled) for users without a
          suppression-request role, per `FindingsPortalController.Suppress` `RequireAnyRole`. */}
      {f.suppressionReason ? (
        <Card>
          <div className={ps.cardBody}>
            <DetailSection title="Suppression">
              <Body2>Reason: {f.suppressionReason}</Body2>
              {f.suppressionJustification && <Body2>{f.suppressionJustification}</Body2>}
              {f.suppressionExpiresAt && (
                <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>
                  Expires {new Date(f.suppressionExpiresAt).toLocaleDateString()}
                </Caption1>
              )}
            </DetailSection>
          </div>
        </Card>
      ) : (
        f.status !== 'Resolved' &&
        canRequestSuppression && (
          <Card>
            <div className={ps.cardBody}>
              <Button appearance="secondary" icon={<DismissCircleRegular />} onClick={handleOpenSuppressDialog}>
                Request Suppression
              </Button>
            </div>
          </Card>
        )
      )}

      {/* Suppression Request Dialog */}
      <Dialog open={suppressDialogOpen} onOpenChange={(_, data) => setSuppressDialogOpen(data.open)}>
        <DialogSurface style={{ maxWidth: '500px' }}>
          <DialogTitle>Request Finding Suppression</DialogTitle>
          <DialogBody>
            <DialogContent>
              <div style={{ display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalM }}>
                <div>
                  <Body2 style={{ fontWeight: 500 }}>{f.title}</Body2>
                  <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>
                    {f.controlId} - {f.frameworkCode}
                  </Caption1>
                </div>

                {suppressError && (
                  <MessageBar intent="error">
                    <MessageBarBody>{suppressError}</MessageBarBody>
                  </MessageBar>
                )}

                <Field label="Reason" required>
                  <Dropdown
                    value={suppressReason}
                    onOptionSelect={(_, data) =>
                      setSuppressReason((data.optionValue || 'FalsePositive') as SuppressionReason)
                    }
                  >
                    <Option value="FalsePositive">False Positive</Option>
                    <Option value="AcceptedRisk">Accepted Risk</Option>
                    <Option value="DuplicateFinding">Duplicate Finding</Option>
                    <Option value="TestEnvironment">Test Environment</Option>
                    <Option value="Other">Other</Option>
                  </Dropdown>
                </Field>

                <Field label="Justification" required>
                  <Textarea
                    placeholder="Explain why this finding should be suppressed..."
                    value={suppressJustification}
                    onChange={(_, data) => setSuppressJustification(data.value)}
                    rows={3}
                  />
                </Field>

                <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>
                  Suppression requests require approval from a second team member (2-person rule).
                </Caption1>
              </div>
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={() => setSuppressDialogOpen(false)}>
                Cancel
              </Button>
              <Button
                appearance="primary"
                onClick={handleSubmitSuppression}
                disabled={!suppressJustification.trim() || suppressMutation.isPending}
              >
                {suppressMutation.isPending ? 'Submitting...' : 'Request Suppression'}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>Reference ID: {f.referenceId}</Caption1>
    </div>
  );
};
